import axios from 'axios';
import { CurrentVersion } from "utils/constants";
import { JiraServerInfoResult } from "utils/interfaces";
import { logSentryError, SentryContext } from "utils/loggingUtil";
import { VersionUtil } from "utils/versionUtil";
import { getUrlParameterByName, toQueryParams } from '@yasoonofficial/runtime';
import { BaseUrl } from 'utils/currentUrls';

export class Environment {
    static isMobileDevice() {
        return (window['Office'] && (this.isIOS() || this.isAndroid()));
    }

    static isIOS() {
        return (window['Office'] && Office.context.mailbox.diagnostics.hostName === 'OutlookIOS');
    }

    static isAndroid() {
        return (window['Office'] && Office.context.mailbox.diagnostics.hostName === 'OutlookAndroid');
    }

    static needsOfficeProxy() {
        // If we are working inside a dialog, we need to mock the Office API
        // because it does not work inside dialogs. We'll message with the dialog
        // parent to execute the actual functions
        if (this.isRunningInDialog())
            return true;

        // Alternative: We are working inside the COM core add-in, then we'll need a proxy
        // everytime (will AJAX into C# code)
        if (this.isWrapperAddin())
            return true;
    }

    static getComSetting(setting: string) {
        if (!this.isWrapperAddin())
            return null;

        return Office.context["comSettings"][setting];
    }

    static getComCallbackUrl() {
        if (!this.getComSetting("usePublicToLocalhostRedirect"))
            return BaseUrl;

        return BaseUrl.replace("http://localhost", "http://local-auth.yasoon.com");
    }

    static getOAuthCallbackUrl(data: { instanceUrl: string, uuid: string, serverInfo?: JiraServerInfoResult; }) {
        let url = BaseUrl;
        let params = {
            instanceUrl: btoa(data.instanceUrl),
            uuid: data.uuid
        };

        if (Environment.isWrapperAddin()) {
            url = this.getComCallbackUrl();
            params["host"] = "com";
            params["comDialogId"] = getUrlParameterByName("comDialogId");
            params["comDialogToken"] = getUrlParameterByName("comDialogToken");
        }

        url += '/authDialog.html?' + toQueryParams(params);

        // New logic for Jira server... When the new app is installed + at least 3.3
        // => Use redirect via Jira Server.. Covers weird edge cases where Jira does not redirect
        //    to the correct base url
        if (data.serverInfo?.version && VersionUtil.isVersionAtLeast(data.serverInfo.version, "3.3.0")) {
            // We need to replace slashes first because Java is incabable of handling PathParams which contain encoded slashes
            // WHY
            let originalUrl = btoa(url).replace(/\//, '-');
            url = data.instanceUrl + '/rest/com.yasoon.jira.outlook-email/1.0/applink/authorize-callback/' + encodeURIComponent(originalUrl);
        }

        return url;
    }

    static getEmailMessageMimeType() {
        if (this.isWrapperAddin()) {
            return {
                extension: '.msg',
                mimeType: 'application/vnd.ms-outlook'
            };
        }
        else {
            return {
                extension: '.eml',
                mimeType: 'message/rfc822'
            };
        }
    }

    static isWrapperAddin() {
        return getUrlParameterByName('host') === 'com';
    }

    static getAddinVersion() {
        return CurrentVersion;
    }

    static globalProxyUrl: string | null = null;
    static getGlobalProxyUrl() {
        if (Environment.isWrapperAddin()) {
            return '/api/jira-proxy/request';
        } else {
            return this.globalProxyUrl ?? 'https://blaine-the-mono.yasoon.com/jiraRequest';
        }
    }

    static getWrapperAuthToken() {
        return btoa(getUrlParameterByName("comDialogId") + ":" + getUrlParameterByName("comDialogToken"));
    }

    static isRunningInDialog() {
        return getUrlParameterByName('env') === 'dialog';
    }

    static openNewWindow(url: string, target?: string) {
        if (this.isWrapperAddin()) {
            axios({
                url: '/api/util/open-browser',
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Basic ${this.getWrapperAuthToken()}`
                },
                data: JSON.stringify({ url: url })
            });
        } else {
            return window.open(url, target);
        }

    }

    static isIE(platform?: string, ua?: string) {
        ua = ua || window.navigator.userAgent;
        platform = platform || 'unknown';

        try {
            let msie = ua.indexOf('MSIE ');
            if (msie > 0) {
                // IE 10 or older => return version number
                return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
            }

            let trident = ua.indexOf('Trident/');
            if (trident > 0) {
                // IE 11 => return version number
                let rv = ua.indexOf('rv:');
                return parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
            }

            let edge = ua.indexOf('Edge/');
            if (edge > 0) {
                // Edge (IE 12+) => return version number
                return parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
            }
        }
        catch (e) {
            logSentryError(e, SentryContext.browser, { message: 'Error while detecting user agent, falling back to legacy' });

            return true;
        }

        // other browser
        return false;
    }

    static isLegacyEdge() {
        const ua = window.navigator.userAgent;
        return ua.includes('Edge/');
    }

    static detectSafari(ua?: string): boolean {
        ua = ua || window.navigator.userAgent;
        if (!ua)
            return false;
        else
            return !!(ua.indexOf('Safari') !== -1 && ua.indexOf('Chrome') === -1);
    }

    static detectOldSafari(platform?: string, ua?: string) {
        ua = ua || window.navigator.userAgent;
        platform = platform || 'unknown';

        if (platform && platform === 'Mac') {
            let safari = ua.indexOf('AppleWebKit/');
            if (safari > 0) {
                //Mozilla/5.0 (Macintosh; Intel Mac OS X 10_12) AppleWebKit/602.1.50 (KHTML, like Gecko)
                let version = parseInt(ua.substring(safari + 12, ua.indexOf('.', safari)));
                return version < 604;
            }
        }

        return false;
    }

    static getPlatform(): string {
        if (Office.context.platform) {
            return Office.context.platform.toString();
        } else if (Office.context.diagnostics && Office.context.diagnostics.platform) {
            return Office.context.diagnostics.platform.toString();
        } else {
            return 'PC';
        }
    }
}
